import { createContext, useContext, useState, useEffect, type ReactNode } from 'react'
import { SPONSORSHIP_AMOUNT_PLS, AVG_GAS_PLS } from '../constants.js'

interface BotStats {
  count: number
  balance: bigint
  address: string
}

interface StatsData {
  bots: {
    bridge: BotStats
    plsx: BotStats
    offRamp: BotStats & { numerator: bigint }
  }
  priceUSD: string
  block: number
}

interface StatsContextType {
  stats: StatsData | null
  loading: boolean
  error: Error | null
  refetch: () => void
  costPerUserUsd: string | null
  remainingBridge: number | null
  remainingPlsx: number | null
}

const StatsContext = createContext<StatsContextType | undefined>(undefined)

const COST_PER_USER_DIVISOR = 10n ** 8n

function computeCostPerUserUsd(priceUSD: string): string {
  // priceUSD = PLS_price * 10^8 * 3690 (cost of one 3690 PLS sponsorship in USD, 8 decimals)
  const value = Number(priceUSD) / 1e8
  return `$${value.toFixed(4)}`
}

function computeRemainingUsers(balance: bigint): number {
  const costPerUser = SPONSORSHIP_AMOUNT_PLS + AVG_GAS_PLS
  if (costPerUser === 0n) return 0
  return Number(balance / costPerUser)
}

export function StatsProvider({ children }: { children: ReactNode }) {
  const [stats, setStats] = useState<StatsData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  const fetchStats = async () => {
    try {
      setLoading(true)
      setError(null)
      const res = await fetch('/api/stats')
      if (!res.ok) {
        throw new Error(`Failed to fetch stats: ${res.statusText}`)
      }
      const data = await res.json()

      // Convert balance strings to BigInt
      const processedData: StatsData = {
        ...data,
        bots: {
          bridge: { ...data.bots.bridge, balance: BigInt(data.bots.bridge.balance) },
          plsx: { ...data.bots.plsx, balance: BigInt(data.bots.plsx.balance) },
          offRamp: {
            ...data.bots.offRamp,
            balance: BigInt(data.bots.offRamp.balance),
            numerator: BigInt(data.bots.offRamp.numerator),
          },
        },
      }
      setStats(processedData)
    } catch (err) {
      setError(err as Error)
      console.error('Error fetching stats:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchStats()

    // Refresh stats every 30 seconds
    const interval = setInterval(fetchStats, 30000)
    return () => clearInterval(interval)
  }, [])

  const costPerUserUsd = stats ? computeCostPerUserUsd(stats.priceUSD) : null
  const remainingBridge = stats ? computeRemainingUsers(stats.bots.bridge.balance) : null
  const remainingPlsx = stats ? computeRemainingUsers(stats.bots.plsx.balance) : null

  return (
    <StatsContext.Provider
      value={{ stats, loading, error, refetch: fetchStats, costPerUserUsd, remainingBridge, remainingPlsx }}
    >
      {children}
    </StatsContext.Provider>
  )
}

export function useStats() {
  const context = useContext(StatsContext)
  if (context === undefined) {
    throw new Error('useStats must be used within a StatsProvider')
  }
  return context
}
